import {useMutation, type UseMutationResult} from '@tanstack/react-query'
import {type AxiosResponse} from 'axios'

import Logger from 'services/Logger'
import {PoliciesService} from 'services/api'
import {
  type ApplicationBypassApiPlatformUpdate,
  type ApplicationBypassMutation,
  type ApplicationBypassUpdateData,
  BypassDataServerRequest,
  type BypassUpdateResource,
  type CustomBypassApiItem,
  type GenericMessageResponse,
  type UrlBypassMutation,
} from 'interfaces'

export const UPDATE_BYPASS_QUERY_KEY = 'updateBypassData'

interface BaseUpdateBypassQueryProps {
  onSuccess: () => void
  onError?: () => void
}

interface UpdateBypassQueryProps extends BaseUpdateBypassQueryProps {
  resource: BypassUpdateResource
}

interface UrlUpdateBypassParams {
  policyName: string
  payload: BypassDataServerRequest
  applicationMutation?: never
  urlMutation: UrlBypassMutation
}

interface ApplicationUpdateBypassParams {
  policyName: string
  payload: BypassDataServerRequest
  applicationMutation: ApplicationBypassMutation
  urlMutation?: never
}

type UpdateBypassParams = UrlUpdateBypassParams | ApplicationUpdateBypassParams

type UpdateBypassResponse = Promise<AxiosResponse<GenericMessageResponse>>

const updateUrlBypassResource = async (
  policyName: string,
  payload: BypassDataServerRequest,
  urlMutation: UrlBypassMutation,
): UpdateBypassResponse => {
  switch (urlMutation.type) {
    case 'reset-to-base':
      return PoliciesService.updateUrlBypass(policyName, {
        data: {inheritsFromBase: true},
      })

    case 'delete-custom':
      if (urlMutation.isInherited) {
        if (!urlMutation.default) {
          throw new Error(
            'Default URL bypass state is required for inherited URL delete',
          )
        }

        return PoliciesService.updateUrlBypass(policyName, {
          data: {
            custom: getUrlCustomBypassItemsFromPayload(payload),
            default: urlMutation.default,
          },
        })
      }

      return PoliciesService.deleteUrlBypass(policyName, {
        data: {custom: {urls: urlMutation.urls}},
      })

    case 'update-default':
      return PoliciesService.updateUrlBypass(policyName, {
        data: {
          custom: getUrlCustomBypassItemsFromPayload(payload),
          default: urlMutation.default,
        },
      })

    case 'upsert-custom': {
      const response = await PoliciesService.updateUrlBypass(policyName, {
        data: {custom: getUrlCustomBypassItemsFromPayload(payload)},
      })

      if (urlMutation.deleteCustomUrls?.length) {
        await PoliciesService.deleteUrlBypass(policyName, {
          data: {custom: {urls: urlMutation.deleteCustomUrls}},
        })
      }

      return response
    }
  }
}

const updateApplicationBypassResource = async (
  policyName: string,
  payload: BypassDataServerRequest,
  applicationMutation: ApplicationBypassMutation,
): UpdateBypassResponse => {
  switch (applicationMutation.type) {
    case 'reset-to-base':
      return PoliciesService.updateApplicationBypass(policyName, {
        data: {inheritsFromBase: true},
      })

    case 'update-default':
      return PoliciesService.updateApplicationBypass(policyName, {
        data: {
          custom: applicationMutation.custom,
          default: applicationMutation.default,
        },
      })

    case 'delete-custom':
      return PoliciesService.deleteApplicationBypass(policyName, {
        data: {custom: applicationMutation.custom},
      })

    case 'upsert-custom': {
      const custom =
        getApplicationCustomBypassItemsFromPayload(payload) ??
        applicationMutation.custom
      const defaultUpdate = applicationMutation.default

      let updateData: ApplicationBypassUpdateData
      if (custom) {
        updateData = {
          custom,
          ...(defaultUpdate && {
            default: defaultUpdate,
          }),
        }
      } else if (defaultUpdate) {
        updateData = {default: defaultUpdate}
      } else {
        // Empty custom PUT detaches an inherited policy when no app entries remain.
        updateData = {custom: {mac: [], windows: []}}
      }

      const response = await PoliciesService.updateApplicationBypass(
        policyName,
        {data: updateData},
      )

      if (applicationMutation.deleteCustom) {
        await PoliciesService.deleteApplicationBypass(policyName, {
          data: {custom: applicationMutation.deleteCustom},
        })
      }

      return response
    }
  }
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

const getUrlCustomBypassItemsFromPayload = (
  payload: BypassDataServerRequest,
): CustomBypassApiItem[] =>
  // The new URL API persists only the custom entries it receives when an
  // inherited policy becomes customized, so use the full effective client list.
  payload.customDomainBypassList.map(({name, note}) => ({
    name,
    note,
  }))

const getApplicationCustomBypassItemsFromPayload = (
  payload: BypassDataServerRequest,
): ApplicationBypassApiPlatformUpdate<CustomBypassApiItem> | undefined => {
  // The new Application API persists only the custom entries it receives when
  // an inherited policy becomes customized, so use the full effective list.
  const mac = payload.customApplicationBypassList.Mac.map(({name, note}) => ({
    name,
    note,
  }))
  const windows = payload.customApplicationBypassList.Windows.map(
    ({name, note}) => ({
      name,
      note,
    }),
  )

  if (mac.length && windows.length) return {mac, windows}
  if (mac.length) return {mac}
  if (windows.length) return {windows}

  return undefined
}

export function useUpdateBypass({
  onSuccess,
  onError,
  resource,
}: UpdateBypassQueryProps): UseMutationResult<
  AxiosResponse<GenericMessageResponse>,
  unknown,
  UpdateBypassParams
> {
  return useMutation(
    async ({
      policyName,
      payload,
      applicationMutation,
      urlMutation,
    }: UpdateBypassParams) => {
      if (resource === 'url') {
        if (!urlMutation) {
          throw new Error(
            'URL bypass mutation metadata is required for URL bypass API updates.',
          )
        }

        return updateUrlBypassResource(policyName, payload, urlMutation)
      }

      if (resource === 'application') {
        if (!applicationMutation) {
          throw new Error(
            'Application bypass mutation metadata is required for Application bypass API updates.',
          )
        }

        return updateApplicationBypassResource(
          policyName,
          payload,
          applicationMutation,
        )
      }

      throw new Error(`Unsupported bypass update resource: ${resource}`)
    },
    {
      onSuccess: onSuccess,
      onError: (error: unknown) => {
        Logger.LogError(toError(error))
        onError?.()
      },
    },
  )
}
